import { useState, useEffect } from "react"
import styled from "styled-components"
import { getCssSizeValue, getPaddingsClassFromData, getPropSafely } from "@/utils/utils"
import GridContainer from "@/components/common/GridContainer"
import Text from "@/components/common/Text"
import useGlobals from "@/hooks/useGlobals"
import { BREAKPOINTS } from "@/styles/themeSettings"

const timeUnitLabels = [
	{ key: "days", label: "days" },
	{ key: "hours", label: "hours" },
	{ key: "minutes", label: "minutes" },
	{ key: "seconds", label: "seconds" },
]

const useMapUnitLabelToTranslatedValue = () => {
	const { daysText, hoursText, minutesText, secondsText } = useGlobals((state) => ({
		daysText: state.daysText,
		hoursText: state.hoursText,
		minutesText: state.minutesText,
		secondsText: state.secondsText,
	}))

	return {
		days: daysText,
		hours: hoursText,
		minutes: minutesText,
		seconds: secondsText,
	}
}

const formatNumber = (num) => num.toString().padStart(2, "0")

const calculateTimeDifference = (date) => {
	const difference = new Date(date) - new Date()

	if (difference <= 0) {
		return { isExpired: true, timeUnits: { days: 0, hours: 0, minutes: 0, seconds: 0 } }
	}

	const seconds = Math.floor((difference / 1000) % 60)
	const minutes = Math.floor((difference / 1000 / 60) % 60)
	const hours = Math.floor((difference / (1000 * 60 * 60)) % 24)
	const days = Math.floor(difference / (1000 * 60 * 60 * 24))

	return {
		isExpired: false,
		timeUnits: { days, hours, minutes, seconds },
	}
}

const CountdownContainer = styled.div``

const Content = styled.div`
	padding: ${getCssSizeValue(48, BREAKPOINTS.MOBILE)} 0 ${getCssSizeValue(54, BREAKPOINTS.MOBILE)};
	background: ${({ theme }) => theme.colors.periwinkle};

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		padding: ${getCssSizeValue(64, BREAKPOINTS.TABLET)} 0 ${getCssSizeValue(64, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding: ${getCssSizeValue(64)} 0 ${getCssSizeValue(80)};
	}
`

const Header = styled(Text)`
	font-weight: ${({ theme }) => theme.fontWeight.bold};
`

const Description = styled(Text)`
	font-weight: ${({ theme }) => theme.fontWeight.medium};
	margin: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)} 0;

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		margin: ${getCssSizeValue(24, BREAKPOINTS.TABLET)} 0;
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		margin: unset;
	}
`

const CountdownTimer = styled(GridContainer)`
	margin-top: ${getCssSizeValue(30)};
`

const CountdownItem = styled.div`
	background: ${({ theme }) => theme.colors.white};
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	padding: ${getCssSizeValue(8, BREAKPOINTS.MOBILE)} ${getCssSizeValue(8, BREAKPOINTS.MOBILE)} ${getCssSizeValue(12, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		padding: ${getCssSizeValue(24, BREAKPOINTS.TABLET)} ${getCssSizeValue(24, BREAKPOINTS.TABLET)}
			${getCssSizeValue(40, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding: ${getCssSizeValue(24)} ${getCssSizeValue(24)} ${getCssSizeValue(40)};
	}
`

const CountdownValue = styled.span``

const CountdownLabel = styled.span`
	text-transform: uppercase;
	margin-bottom: ${getCssSizeValue(116)};

	.text {
		font-weight: 500;
	}
`

const TimeUnit = ({ value, label, labelsMap }) => {
	const labelText = labelsMap[label] || label

	return (
		<CountdownItem className="col-mobile-2 col-desktop-2">
			<CountdownLabel>
				<Text className="typo-strawford typo-strawford-09">{labelText}</Text>
			</CountdownLabel>
			<CountdownValue>
				<Text className="typo-borna typo-borna-131">{formatNumber(value)}</Text>
			</CountdownValue>
		</CountdownItem>
	)
}

const formatData = (data) => {
	return {
		header: getPropSafely(data, "header"),
		description: getPropSafely(data, "description"),
		date: getPropSafely(data, "date"),
		paddings: getPaddingsClassFromData(data),
	}
}

const CountDown = ({ data }) => {
	const { header, description, date, paddings } = formatData(data)
	const labelsMap = useMapUnitLabelToTranslatedValue()

	const [timeState, setTimeState] = useState({
		isExpired: false,
		timeUnits: { days: 0, hours: 0, minutes: 0, seconds: 0 },
	})

	useEffect(() => {
		const updateCountdown = () => setTimeState(calculateTimeDifference(date))

		updateCountdown()
		const timer = setInterval(updateCountdown, 1000)

		return () => clearInterval(timer)
	}, [date])

	const { timeUnits } = timeState

	return (
		<CountdownContainer className={paddings}>
			<Content>
				<GridContainer>
					<Header tag="div" className="col-desktop-5 col-tablet-5 col-mobile-8 typo-borna typo-borna-132">
						{header}
					</Header>
				</GridContainer>
				<GridContainer>
					<Description tag="div" className="col-desktop-8 col-tablet-8 col-mobile-8 typo-borna typo-borna-132">
						{description}
					</Description>
				</GridContainer>

				<CountdownTimer className="indent-desktop-4">
					{timeUnitLabels.map(({ key, label }) => (
						<TimeUnit key={key} value={timeUnits[key]} label={label} labelsMap={labelsMap} />
					))}
				</CountdownTimer>

				{/*{isExpired ? (*/}
				{/*	<CountdownExpired>EXPIRED</CountdownExpired>*/}
				{/*) : (*/}
				{/*	<CountdownTimer className='indent-desktop-4'>*/}
				{/*		{timeUnitLabels.map(({key, label}) => (*/}
				{/*			<TimeUnit key={key} value={timeUnits[key]} label={label} labelsMap={labelsMap}/>*/}
				{/*		))}*/}
				{/*	</CountdownTimer>*/}
				{/*)}*/}
			</Content>
		</CountdownContainer>
	)
}

export default CountDown
